import { useEffect, useRef, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';

import { emissionReportImportsApi } from '../../services/emissionReportImportsApi.js';
import type { EmissionReportMassFlow } from '../../types/entities';

/**
 * Die Mittel EINER Massenstrom-Komponente, wie sie angezeigt werden -- dieselben beiden Felder
 * wie `EmissionReportMassFlow.averageCompound`/`averageDirect`, aber ohne die Einzelwerte, die
 * dieser Hook nicht braucht.
 */
export interface MassFlowAverageValue {
  averageCompound: string | null;
  averageDirect: string | null;
}

function extractAverages(
  massFlows: Record<string, EmissionReportMassFlow> | []
): Record<string, MassFlowAverageValue> {
  if (Array.isArray(massFlows)) {
    return {};
  }
  const result: Record<string, MassFlowAverageValue> = {};
  for (const [key, flow] of Object.entries(massFlows)) {
    result[key] = { averageCompound: flow.averageCompound, averageDirect: flow.averageDirect };
  }
  return result;
}

/**
 * Live-Mittel der Massenstrom-Einzelwerte in der offenen Vorschau (Phase 40, Plan 40-09,
 * D-17/T-40-26). Der Hook RECHNET NICHTS -- Mittel sind ausschliesslich eine Servergroesse
 * (Projektregel: vom Server gelieferte Aggregate nicht im Frontend nachrechnen; belegt in
 * `EmissionReportMassFlowAverager`, Plan 40-04/40-05). Er ruft NUR den zustandslosen Endpunkt
 * `POST /api/emission_report_imports/mass_flow_averages` (`emissionReportImportsApi.massFlowAverages`,
 * Plan 40-08) und uebernimmt dessen Antwort unveraendert.
 *
 * "Vor der ersten Antwort die Mittel aus dem uebergebenen `massFlows`" (D-17-Vertrag) heisst hier
 * praezise: der Anfangsstand (`resultData.massFlows[...].averageCompound`/`averageDirect`, beim
 * Laden vom Server mitgeliefert) wird SOFORT gezeigt, ohne dafuer selbst einen Netzaufruf
 * auszuloesen -- ein Aufruf beim blossen Oeffnen der Vorschau waere reine Verschwendung (T-40-26),
 * der Server hat diese Zahlen dem Dokument ja schon mitgegeben. Ein Netzaufruf startet erst, wenn
 * sich `massFlows` seit dem allerersten Aufruf dieses Hooks tatsaechlich veraendert hat (eine
 * Bearbeitung), und dann erst nach 300 ms Ruhe (Entprellmuster aus `useReportCalc.ts`).
 *
 * `enabled=false` (Protokollmodus, T-40-26): kein Netzaufruf, die eingefrorenen Mittel aus
 * `massFlows` bleiben unveraendert stehen -- im Protokoll aendert sich `massFlows` ohnehin nie.
 *
 * Ein Fehler des Endpunkts laesst die zuletzt bekannten Mittel unveraendert stehen (kein Absturz,
 * keine Leerung der Anzeige) -- derselbe Grundsatz wie `useReportCalc`s Catch-Zweig.
 */
export function useMassFlowAverages(
  massFlows: Record<string, EmissionReportMassFlow> | [],
  enabled: boolean
): Record<string, MassFlowAverageValue> {
  const [averages, setAverages] = useState<Record<string, MassFlowAverageValue>>(() =>
    extractAverages(massFlows)
  );

  const key = JSON.stringify(massFlows);
  const [debouncedKey] = useDebouncedValue(key, 300);

  // Der Schluessel des allerersten Renders -- solange `debouncedKey` noch genau diesem Wert
  // entspricht, hat sich seit dem Laden nichts geaendert, und es gibt nichts, wofuer sich ein
  // Netzaufruf lohnen wuerde (siehe Docblock).
  const mountKeyRef = useRef(key);
  // Live-Referenz auf den aktuellen `massFlows`-Wert (analog `valuesRef` in `useReportCalc.ts`):
  // der Effekt haengt nur an `debouncedKey`/`enabled`, braucht zum Zeitpunkt seines Laufs aber den
  // TATSAECHLICHEN, aktuellen `massFlows`-Stand -- nicht den zum Renderzeitpunkt des Effekt-Setups.
  const massFlowsRef = useRef(massFlows);
  massFlowsRef.current = massFlows;
  // Fuer den Abbruch veralteter Antworten: nur die Antwort zum JUENGSTEN Schluessel wird
  // uebernommen (T-40-26).
  const latestKeyRef = useRef(key);
  latestKeyRef.current = key;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (debouncedKey === mountKeyRef.current) {
      return;
    }
    let cancelled = false;
    const requestedKey = debouncedKey;
    const requestMassFlows = massFlowsRef.current;
    emissionReportImportsApi
      .massFlowAverages(requestMassFlows)
      .then((result) => {
        if (cancelled || latestKeyRef.current !== requestedKey) {
          return;
        }
        setAverages(extractAverages(result));
      })
      .catch(() => {
        // Fehler -> letzte bekannte Mittel bleiben stehen, kein Absturz (siehe Docblock).
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedKey, enabled]);

  return averages;
}
